/**
 * Credits readouts — the compact chip in the chat header and the detailed
 * section in AI settings, over one subscription to the credits store.
 *
 * Both render nothing at all unless there is a real balance to report. The
 * credits API is newer than this editor, so an environment without it (or a
 * user without a team) must look exactly like the editor did before credits
 * existed — an empty box saying "unavailable" is worse than no box.
 */

import React, { useEffect, useState } from 'react';
import {
    availableCredits,
    formatCredits,
    getCreditsState,
    isOverdrawn,
    onCreditsChange,
    refreshCredits,
    type CreditsState,
    type TeamCredits,
} from './credits';
import * as styles from './chat-styles';

/** Subscribe to the credits store, kicking off a fetch on first mount. */
function useCredits(): CreditsState {
    const [state, setState] = useState<CreditsState>(getCreditsState);
    useEffect(() => {
        const unsubscribe = onCreditsChange(setState);
        void refreshCredits();
        return unsubscribe;
    }, []);
    return state;
}

/** "resets 12 Aug" — the monthly window's rollover, omitted when unknown. */
function formatResetsAt(iso: string | null): string | null {
    if (!iso) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function tooltipFor(credits: TeamCredits): string {
    const { balance, monthly } = credits;
    const lines = [`${formatCredits(balance)} credits available`];
    if (monthly.allotment > 0) {
        lines.push(`${formatCredits(monthly.used)} of ${formatCredits(monthly.allotment)} used this month`);
    } else if (monthly.used > 0) {
        lines.push(`${formatCredits(monthly.used)} used this month`);
    }
    const resets = formatResetsAt(monthly.resetsAt);
    if (resets) lines.push(`Resets ${resets}`);
    if (isOverdrawn(credits)) lines.push('Out of credits — top up in Billing');
    return lines.join('\n');
}

/**
 * Compact header indicator: available credits, dimmed like the model string
 * next to it. Turns red once the balance is spent, which is the only state
 * worth interrupting someone mid-scene for.
 */
export const CreditsChip: React.FC = () => {
    const state = useCredits();
    if (state.status !== 'ready') return null;
    const { credits } = state;
    const spent = isOverdrawn(credits);
    return (
        <span
            title={tooltipFor(credits)}
            style={{
                fontSize: 10,
                color: spent ? styles.colors.error : styles.colors.textMuted,
                opacity: spent ? 1 : 0.7,
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                userSelect: 'none',
                whiteSpace: 'nowrap',
            }}
        >
            {formatCredits(availableCredits(credits))} cr
        </span>
    );
};

interface CreditsSectionProps {
    /** Row label styling, which carries the shared column width so rows line up. */
    sectionLabelStyle: React.CSSProperties;
    rowLabelStyle: React.CSSProperties;
}

/**
 * Settings-panel breakdown: what's left, what this month cost, when it rolls
 * over. Shown only when the ledger answered.
 */
export const CreditsSection: React.FC<CreditsSectionProps> = ({ sectionLabelStyle, rowLabelStyle }) => {
    const state = useCredits();
    if (state.status !== 'ready') return null;

    const { credits } = state;
    const spent = isOverdrawn(credits);
    const resets = formatResetsAt(credits.monthly.resetsAt);
    const value: React.CSSProperties = { fontSize: 11, color: styles.colors.text };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            paddingTop: 12,
            borderTop: `1px solid ${styles.colors.border}`,
        }}>
            <label style={sectionLabelStyle}>Credits</label>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={rowLabelStyle}>Available</span>
                <span style={{ ...value, color: spent ? styles.colors.error : styles.colors.text, fontWeight: 500 }}>
                    {formatCredits(availableCredits(credits))}
                </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={rowLabelStyle}>Used</span>
                <span style={value}>
                    {credits.monthly.allotment > 0
                        ? `${formatCredits(credits.monthly.used)} of ${formatCredits(credits.monthly.allotment)} this month`
                        : `${formatCredits(credits.monthly.used)} this month`}
                </span>
            </div>

            {resets && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={rowLabelStyle}>Resets</span>
                    <span style={value}>{resets}</span>
                </div>
            )}

            <span style={{ fontSize: 10, color: spent ? styles.colors.error : styles.colors.textMuted, lineHeight: 1.4 }}>
                {spent
                    ? 'Out of credits — top up in Billing to keep using Trace AI.'
                    : 'Trace AI usage is billed to the team selected in the catalog.'}
            </span>
        </div>
    );
};
